import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { GnarlyWordmark } from "./premium/Logo";

/**
 * Slim site-wide footer mounted at the bottom of every page.
 * Hosts brand, social links (Telegram + X), and entry points to the
 * Disclaimer and Terms of Service modals.
 */
export default function SiteFooter() {
  const [open, setOpen] = useState(null); // 'disclaimer' | 'terms' | null

  return (
    <>
      <footer className="pm-footer">
        <div className="pm-footer__brand">
          <GnarlyWordmark size={18} />
          <span className="pm-footer__tag">direct-to-DEX on Solana</span>
        </div>

        <div className="pm-footer__links">
          <button
            type="button"
            className="pm-footer__link"
            onClick={() => setOpen("disclaimer")}
          >
            Disclaimer
          </button>
          <button
            type="button"
            className="pm-footer__link"
            onClick={() => setOpen("terms")}
          >
            Terms
          </button>
          <Link
            className="pm-footer__link"
            to="/docs"
          >
            Docs
          </Link>
          <Link
            className="pm-footer__link"
            to="/developers"
          >
            Developers
          </Link>
          <a
            className="pm-footer__link"
            href="https://t.me/Slurp_Support_bot"
            target="_blank"
            rel="noreferrer"
          >
            Support
          </a>
        </div>

        <div className="pm-footer__socials">
          <a
            href="https://t.me/slurpdotgg"
            target="_blank"
            rel="noreferrer"
            className="pm-footer__social"
            aria-label="Slurp on Telegram"
            title="Telegram"
          >
            <TelegramIcon />
          </a>
          <a
            href="https://x.com/slurpdotgg"
            target="_blank"
            rel="noreferrer"
            className="pm-footer__social"
            aria-label="Slurp on X"
            title="X"
          >
            <XIcon />
          </a>
        </div>
      </footer>

      {open === "disclaimer" && (
        <FooterModal title="Disclaimer" onClose={() => setOpen(null)}>
          <p>
            slurp.gg is a non-custodial software interface to the Solana
            blockchain. Tokens launched, traded, or held through the
            interface are user-created digital assets, not securities, not
            investment products, and not regulated financial instruments.
            Nothing on slurp.gg should be interpreted as an offer, sale,
            or solicitation of any security or financial product.
          </p>
          <p>
            All tokens accessible via slurp.gg are highly speculative and
            volatile. You may lose some or all of the funds you commit. The
            platform does not vet, audit, or endorse any token, creator, or
            community. Listings are user-generated and may include scams,
            rug pulls, impersonations, or otherwise misleading content.
          </p>
          <p>
            Prices, liquidity, and on-chain conditions can change without
            warning. Smart-contract bugs, oracle failures, MEV, sandwich
            attacks, RPC outages, network congestion, or third-party
            service disruptions can result in delayed, failed, or partial
            transactions and total loss of funds. You acknowledge and
            accept these risks.
          </p>
          <p>
            slurp.gg provides no investment, legal, accounting, or tax
            advice. You are solely responsible for determining whether use
            of the interface is legal in your jurisdiction and for
            complying with all applicable laws, including tax reporting.
            Conduct your own research before interacting with any token.
          </p>
        </FooterModal>
      )}

      {open === "terms" && (
        <FooterModal title="Terms of Service" onClose={() => setOpen(null)}>
          <p>
            <b>Last updated: 2026-05-06.</b> These Terms govern your access
            to and use of the slurp.gg website and any related software
            interfaces (the "Interface"). By accessing or using the
            Interface in any way you agree to be bound by these Terms. If
            you do not agree, do not use the Interface.
          </p>
          <p>
            <b>1. Nature of the Interface.</b> The Interface is a purely
            non-custodial, front-end software tool that allows users to
            interact with the Solana blockchain and with smart contracts
            developed by third parties (including Meteora). slurp.gg does
            not operate an exchange, broker-dealer, money transmitter, or
            custodial wallet service. slurp.gg never takes possession or
            control of user funds at any time.
          </p>
          <p>
            <b>2. Eligibility and Jurisdiction.</b> You represent and
            warrant that (a) you are at least the age of majority in your
            jurisdiction; (b) you are not a resident of, located in, or a
            citizen of any country, region, or territory subject to
            comprehensive sanctions imposed by the U.S. Office of Foreign
            Assets Control (OFAC), the United Nations, the European Union,
            or the United Kingdom, including but not limited to Cuba, Iran,
            North Korea, Syria, the Crimea, Donetsk, and Luhansk regions;
            (c) you are not on any list of restricted or prohibited
            persons maintained by any government authority; and (d) your
            use of the Interface does not violate any applicable law,
            regulation, or rule in your jurisdiction. You are responsible
            for confirming the legality of your use of the Interface.
          </p>
          <p>
            <b>3. No Investment Advice; No Endorsement.</b> Nothing
            displayed in or accessible through the Interface constitutes
            investment, legal, accounting, or tax advice. Tokens accessible
            via the Interface are user-created and are not vetted, audited,
            endorsed, or recommended by slurp.gg. Any references to
            specific tokens, market data, prices, or activity are
            informational only.
          </p>
          <p>
            <b>4. Self-Custody and User Responsibility.</b> You alone
            control your wallet, private keys, seed phrases, and on-chain
            approvals. slurp.gg cannot recover lost keys, reverse
            transactions, refund losses, or restore access. You are solely
            responsible for the security of your devices, software, and
            credentials, and for verifying every transaction before
            signing.
          </p>
          <p>
            <b>5. Risk Acknowledgement.</b> You acknowledge that
            participation in token launches, trading, holding,
            communities, or other features carries significant risks
            including but not limited to: total loss of funds; smart-
            contract vulnerabilities; protocol or oracle failures; MEV,
            sandwich attacks, and front-running; liquidity withdrawal,
            depeg events, or rug pulls executed by token creators or third
            parties; impersonation, phishing, and social-engineering
            attacks; tax exposure; regulatory action against tokens or
            their creators; and disruption of third-party services such as
            RPC providers, hosting, or wallet adapters. You accept all
            such risks.
          </p>
          <p>
            <b>6. Fees.</b> A flat 1% trading fee applies on every swap and
            is split among the token creator, slurp.gg, Meteora, the
            weekly Diamond Hands prize pool, and the monthly site-wide
            jackpot, as described in the public documentation. Solana
            network fees and other on-chain costs are paid directly by
            you to the network and are outside the control of slurp.gg.
          </p>
          <p>
            <b>7. Third-Party Services.</b> The Interface integrates with
            independent third-party services and protocols (including but
            not limited to Solana, Meteora, Helius, Cloudflare, wallet
            providers, and indexers). slurp.gg does not control, endorse,
            or guarantee any third-party service and is not liable for
            their availability, accuracy, security, or conduct.
          </p>
          <p>
            <b>8. No Warranty.</b> THE INTERFACE IS PROVIDED "AS IS" AND
            "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EXPRESS OR
            IMPLIED, INCLUDING WITHOUT LIMITATION ANY WARRANTY OF
            MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE,
            NON-INFRINGEMENT, ACCURACY, OR UNINTERRUPTED AVAILABILITY.
            slurp.gg does not warrant that the Interface will be free from
            errors, bugs, security vulnerabilities, or downtime, or that
            data displayed will be accurate or current.
          </p>
          <p>
            <b>9. Limitation of Liability.</b> TO THE MAXIMUM EXTENT
            PERMITTED BY APPLICABLE LAW, IN NO EVENT WILL slurp.gg, ITS
            OPERATORS, AFFILIATES, CONTRIBUTORS, OR AGENTS BE LIABLE FOR
            ANY DIRECT, INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL,
            EXEMPLARY, OR PUNITIVE DAMAGES, INCLUDING WITHOUT LIMITATION
            LOST PROFITS, LOST DATA, LOST TOKENS, LOSS OF GOODWILL, OR
            BUSINESS INTERRUPTION, ARISING OUT OF OR IN CONNECTION WITH
            YOUR USE OF THE INTERFACE OR ANY TOKEN ACCESSED THROUGH IT,
            WHETHER BASED ON CONTRACT, TORT, NEGLIGENCE, STRICT LIABILITY,
            OR ANY OTHER LEGAL THEORY, AND WHETHER OR NOT slurp.gg HAS
            BEEN ADVISED OF THE POSSIBILITY OF SUCH DAMAGES. THE TOTAL
            AGGREGATE LIABILITY OF slurp.gg TO ANY USER FOR ANY CLAIM
            SHALL NOT EXCEED ONE HUNDRED U.S. DOLLARS ($100).
          </p>
          <p>
            <b>10. Indemnification.</b> You agree to defend, indemnify, and
            hold harmless slurp.gg, its operators, affiliates, contributors,
            and agents from and against any and all claims, damages,
            obligations, losses, liabilities, costs, and expenses
            (including reasonable attorneys' fees) arising from: (a) your
            use of or access to the Interface; (b) your violation of these
            Terms; (c) your violation of any third-party right, including
            intellectual property, privacy, or contractual rights; (d) any
            content you submit, post, or transmit through the Interface
            including communities, threads, comments, and token metadata;
            and (e) any token you create, launch, promote, or trade.
          </p>
          <p>
            <b>11. Force Majeure.</b> slurp.gg is not liable for any
            failure or delay in performance caused by circumstances beyond
            its reasonable control, including but not limited to acts of
            God, war, terrorism, civil unrest, pandemic, governmental
            action, network failure, blockchain congestion, hacking, or
            third-party service disruption.
          </p>
          <p>
            <b>12. Dispute Resolution; Arbitration; Class-Action Waiver.</b>
            Any dispute, claim, or controversy arising out of or relating
            to these Terms or the Interface shall be resolved exclusively
            through binding individual arbitration administered under the
            commercial arbitration rules of a recognized arbitration body
            in a neutral jurisdiction. YOU AGREE THAT ANY DISPUTE WILL BE
            BROUGHT ON AN INDIVIDUAL BASIS ONLY AND NOT AS A PLAINTIFF OR
            CLASS MEMBER IN ANY PURPORTED CLASS, COLLECTIVE,
            REPRESENTATIVE, OR CONSOLIDATED PROCEEDING. The arbitrator may
            award only individual relief and may not consolidate or
            aggregate claims. You waive any right to a trial by jury.
          </p>
          <p>
            <b>13. Governing Law.</b> These Terms are governed by the laws
            of the jurisdiction of slurp.gg's principal place of operation,
            without regard to conflict-of-laws principles.
          </p>
          <p>
            <b>14. Termination.</b> slurp.gg may suspend, restrict, or
            terminate your access to the Interface at any time, for any
            reason or no reason, without notice or liability. The smart
            contracts and on-chain assets you interact with remain
            accessible directly on Solana regardless of the Interface
            being available.
          </p>
          <p>
            <b>15. Privacy.</b> The Interface does not require account
            creation. Wallet addresses, on-chain activity, and any
            content you voluntarily post (profiles, communities, replies)
            are public and indelible. Do not post information you do not
            wish to be public and permanent.
          </p>
          <p>
            <b>16. Changes to Terms.</b> slurp.gg may modify these Terms at
            any time by posting an updated version. Continued use of the
            Interface after a change constitutes acceptance of the
            updated Terms. The "Last updated" date above reflects the
            effective date of the current version.
          </p>
          <p>
            <b>17. Severability; Entire Agreement.</b> If any provision of
            these Terms is held unenforceable, the remaining provisions
            remain in full effect. These Terms together with the Disclaimer
            constitute the entire agreement between you and slurp.gg
            concerning the Interface and supersede any prior or
            contemporaneous communications.
          </p>
        </FooterModal>
      )}
    </>
  );
}

function FooterModal({ title, onClose, children }) {
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <div className="pm-footer-modal__backdrop" onClick={onClose}>
      <div
        className="pm-footer-modal"
        role="dialog"
        aria-modal="true"
        aria-label={title}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="pm-footer-modal__head">
          <span className="pm-footer-modal__title">{title}</span>
          <button
            type="button"
            className="pm-footer-modal__close"
            onClick={onClose}
            aria-label="Close"
          >
            ×
          </button>
        </div>
        <div className="pm-footer-modal__body">{children}</div>
      </div>
    </div>
  );
}

function TelegramIcon() {
  return (
    <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor" aria-hidden="true">
      <path d="M9.78 16.72 9.62 20.4c.51 0 .73-.22 1-.49l2.4-2.3 4.97 3.64c.91.5 1.56.24 1.81-.84l3.28-15.4h.01c.29-1.35-.49-1.88-1.38-1.55L1.43 9.92C.1 10.45.12 11.21 1.2 11.55l4.99 1.55 11.59-7.31c.55-.36 1.04-.16.64.2L9.78 16.72Z" />
    </svg>
  );
}

function XIcon() {
  return (
    <svg viewBox="0 0 24 24" width="14" height="14" fill="currentColor" aria-hidden="true">
      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231L18.244 2.25Zm-1.161 17.52h1.833L7.084 4.126H5.117L17.083 19.77Z" />
    </svg>
  );
}
